import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getSwingAlerts, ackSwingAlert, dismissSwingAlert } from '../../api/client';

const SEVERITY_STYLE = {
  HIGH:  { bg: '#2a1010', color: '#f44336', border: '#5a2020', label: 'HIGH' },
  WARN:  { bg: '#2a1a00', color: '#FF9800', border: '#5a3a00', label: 'WARN' },
  INFO:  { bg: '#001a2a', color: '#2196F3', border: '#003a5a', label: 'INFO' },
};

const severityOrder = { HIGH: 0, WARN: 1, INFO: 2 };

const ALERT_TYPES = ['ALL', 'SL_HIT', 'TARGET_HIT', 'REBALANCE', 'SCAN_DONE', 'ENTRY_SIGNAL', 'OTHER'];

function AlertCard({ alert, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const sev = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.INFO;

  const handleAck = async () => {
    setBusy(true);
    try { await ackSwingAlert(alert.id); onRefresh(); } finally { setBusy(false); }
  };

  const handleDismiss = async () => {
    setBusy(true);
    try { await dismissSwingAlert(alert.id); onRefresh(); } finally { setBusy(false); }
  };

  const timeStr = alert.created_at
    ? new Date(alert.created_at).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })
    : '';

  return (
    <div style={{
      border: `1px solid ${alert.acknowledged ? '#222' : sev.border}`,
      borderLeft: `3px solid ${alert.acknowledged ? '#333' : sev.color}`,
      background: alert.acknowledged ? '#141414' : sev.bg,
      borderRadius: 8,
      padding: '12px 16px',
      marginBottom: 10,
      opacity: alert.acknowledged ? 0.65 : 1,
      transition: 'opacity 0.2s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{
              padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 700,
              background: sev.bg, color: sev.color, border: `1px solid ${sev.border}`,
            }}>
              {sev.label}
            </span>
            {alert.alert_type && (
              <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, background: '#2a2a2a', color: '#aaa', border: '1px solid #444' }}>
                {alert.alert_type}
              </span>
            )}
            {alert.symbol && (
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#fff' }}>
                {alert.symbol}
              </span>
            )}
            {!alert.acknowledged && (
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: sev.color, display: 'inline-block' }} />
            )}
          </div>
          <div style={{ fontSize: 13, color: alert.acknowledged ? '#666' : '#ccc', lineHeight: 1.5 }}>
            {alert.message}
          </div>
          <div style={{ fontSize: 11, color: '#555', marginTop: 6 }}>{timeStr}</div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {!alert.acknowledged && (
            <button
              onClick={handleAck}
              disabled={busy}
              style={{ padding: '4px 10px', fontSize: 11, background: '#1a3a1a', border: '1px solid #2d6a2d', color: '#4CAF50', borderRadius: 4, cursor: 'pointer' }}
            >
              Ack
            </button>
          )}
          <button
            onClick={handleDismiss}
            disabled={busy}
            style={{ padding: '4px 10px', fontSize: 11, background: '#2a1010', border: '1px solid #5a2020', color: '#f44', borderRadius: 4, cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SwingAlerts() {
  const [alerts, setAlerts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [ackingAll, setAckingAll] = useState(false);
  const intervalRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (unreadOnly) params.acknowledged = false;
      if (typeFilter !== 'ALL') params.alert_type = typeFilter;
      const res = await getSwingAlerts(params);
      const raw = res.data.alerts || [];
      // Sort: unread first, then by time desc
      raw.sort((a, b) => {
        const unreadDiff = (a.acknowledged ? 1 : 0) - (b.acknowledged ? 1 : 0);
        if (unreadDiff !== 0) return unreadDiff;
        const sevDiff = (severityOrder[a.severity] || 2) - (severityOrder[b.severity] || 2);
        if (sevDiff !== 0) return sevDiff;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setAlerts(raw);
    } catch {
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [unreadOnly, typeFilter]);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(load, 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const handleAckAll = async () => {
    const unread = alerts.filter(a => !a.acknowledged);
    if (unread.length === 0) return;
    setAckingAll(true);
    try {
      await Promise.all(unread.map(a => ackSwingAlert(a.id)));
      await load();
    } finally {
      setAckingAll(false);
    }
  };

  const unreadCount = alerts.filter(a => !a.acknowledged).length;

  return (
    <div>
      {error && <div style={{ color: '#f44', marginBottom: 12, padding: '8px 12px', background: '#2a1010', borderRadius: 6 }}>{error}</div>}

      {/* Controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {ALERT_TYPES.map(type => (
              <button
                key={type}
                onClick={() => setTypeFilter(type)}
                style={{
                  padding: '4px 10px', borderRadius: 99, fontSize: 11, cursor: 'pointer',
                  background: typeFilter === type ? '#2196F3' : '#1e1e1e',
                  color: typeFilter === type ? '#fff' : '#aaa',
                  border: typeFilter === type ? '1px solid #2196F3' : '1px solid #444',
                  fontWeight: typeFilter === type ? 600 : 400,
                }}
              >
                {type}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#888', cursor: 'pointer' }}>
            <input type="checkbox" checked={unreadOnly} onChange={e => setUnreadOnly(e.target.checked)} />
            Unread only
          </label>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 10, alignItems: 'center' }}>
            {unreadCount > 0 && (
              <span style={{ fontSize: 12, color: '#f44' }}>{unreadCount} unread</span>
            )}
            <button
              onClick={handleAckAll}
              disabled={ackingAll || unreadCount === 0}
              style={{
                padding: '6px 14px', fontSize: 12, background: unreadCount > 0 ? '#1a3a1a' : '#1a1a1a',
                border: `1px solid ${unreadCount > 0 ? '#2d6a2d' : '#333'}`,
                color: unreadCount > 0 ? '#4CAF50' : '#555',
                borderRadius: 6, cursor: unreadCount > 0 ? 'pointer' : 'not-allowed',
              }}
            >
              {ackingAll ? 'Acking...' : 'Ack All'}
            </button>
          </div>
        </div>
      </div>

      {/* Alert feed */}
      {loading ? (
        <div style={{ padding: 24, color: '#888', textAlign: 'center' }}>Loading alerts...</div>
      ) : alerts.length === 0 ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#666' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔔</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No alerts</div>
          <div style={{ fontSize: 13, color: '#555' }}>Swing Trader alerts (SL hits, target hits, rebalance signals) will appear here.</div>
        </div>
      ) : (
        <div>
          {alerts.map(alert => (
            <AlertCard key={alert.id} alert={alert} onRefresh={load} />
          ))}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: '#444', textAlign: 'right' }}>
        Auto-refreshes every 30 seconds
      </div>
    </div>
  );
}
